import React from 'react';
import { useRouter } from './Router';
import { useLanguage } from '../contexts/languageStore';

interface LinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  activeClassName?: string;
  preserveLanguage?: boolean; // If true, keeps current language prefix
}

export function Link({
  to,
  children,
  className = '',
  activeClassName = 'active',
  preserveLanguage = true
}: LinkProps) {
  const { pathname, navigate } = useRouter();
  const { language } = useLanguage();

  // Handle language prefixes
  const getLocalizedPath = (path: string): string => {
    if (!preserveLanguage) return path;

    // If path already has language prefix, return as is
    if (path.startsWith('/ar/') || path.startsWith('/en/')) {
      return path;
    }

    // If path is root, add language prefix
    if (path === '/') {
      return language === 'ar' ? '/ar' : '/en';
    }

    // Add language prefix to other paths
    return language === 'ar' ? `/ar${path}` : `/en${path}`;
  };

  const localizedTo = getLocalizedPath(to);
  const isActive = pathname === localizedTo;
  const linkClassName = `${className} ${isActive ? activeClassName : ''}`.trim();

  return (
    <a
      href={localizedTo}
      className={linkClassName}
      onClick={(e) => {
        e.preventDefault();
        navigate(localizedTo);
      }}
      aria-current={isActive ? 'page' : undefined}
    >
      {children}
    </a>
  );
}
